import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Plus, Edit2, Trash2, Search, Beaker, AlertTriangle, ExternalLink, X } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';

const CATEGORIES = [
  { value: 'insecticide', label: 'Insecticide' },
  { value: 'herbicide', label: 'Herbicide' },
  { value: 'fungicide', label: 'Fungicide' },
  { value: 'rodenticide', label: 'Rodenticide' },
  { value: 'termiticide', label: 'Termiticide' },
  { value: 'disinfectant', label: 'Disinfectant' },
  { value: 'fertilizer', label: 'Fertilizer' },
  { value: 'general', label: 'General' }
];

const SIGNAL_WORDS = [
  { value: '', label: 'None' },
  { value: 'CAUTION', label: 'CAUTION' },
  { value: 'WARNING', label: 'WARNING' },
  { value: 'DANGER', label: 'DANGER' }
];

const UNITS = ['oz', 'ml', 'gallon', 'liter', 'lb', 'kg', 'gram', 'quart', 'pint'];

export default function ChemicalManagement({ companyId }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();

  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingChemical, setEditingChemical] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    manufacturer: '',
    epa_number: '',
    active_ingredient: '',
    category: 'general',
    signal_word: '',
    default_unit: 'oz',
    cost_per_unit: '',
    sds_url: '',
    notes: ''
  });

  useEffect(() => {
    loadChemicals();
  }, [companyId]);

  const loadChemicals = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('chemicals')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setChemicals(data || []);
    } catch (err) {
      showError('Failed to load chemicals: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      manufacturer: '',
      epa_number: '',
      active_ingredient: '',
      category: 'general',
      signal_word: '',
      default_unit: 'oz',
      cost_per_unit: '',
      sds_url: '',
      notes: ''
    });
    setEditingChemical(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (chemical) => {
    setEditingChemical(chemical);
    setFormData({
      name: chemical.name || '',
      manufacturer: chemical.manufacturer || '',
      epa_number: chemical.epa_number || '',
      active_ingredient: chemical.active_ingredient || '',
      category: chemical.category || 'general',
      signal_word: chemical.signal_word || '',
      default_unit: chemical.default_unit || 'oz',
      cost_per_unit: chemical.cost_per_unit || '',
      sds_url: chemical.sds_url || '',
      notes: chemical.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showAlert('Chemical name is required');
      return;
    }

    setSaving(true);
    try {
      const chemicalData = {
        company_id: companyId,
        name: formData.name.trim(),
        manufacturer: formData.manufacturer.trim() || null,
        epa_number: formData.epa_number.trim() || null,
        active_ingredient: formData.active_ingredient.trim() || null,
        category: formData.category,
        signal_word: formData.signal_word || null,
        default_unit: formData.default_unit,
        cost_per_unit: formData.cost_per_unit ? parseFloat(formData.cost_per_unit) : null,
        sds_url: formData.sds_url.trim() || null,
        notes: formData.notes.trim() || null,
        updated_at: new Date().toISOString()
      };

      if (editingChemical) {
        const { error } = await supabase
          .from('chemicals')
          .update(chemicalData)
          .eq('id', editingChemical.id);

        if (error) throw error;
        showSuccess('Chemical updated');
      } else {
        const { error } = await supabase
          .from('chemicals')
          .insert([chemicalData]);

        if (error) throw error;
        showSuccess('Chemical added');
      }

      setShowModal(false);
      resetForm();
      loadChemicals();
    } catch (err) {
      showError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (chemical) => {
    const confirmed = await showConfirm(
      `Delete "${chemical.name}"? This will remove it from your chemical library.`,
      { title: 'Delete Chemical', confirmText: 'Delete', confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    try {
      // Soft delete - just mark as inactive
      const { error } = await supabase
        .from('chemicals')
        .update({ is_active: false })
        .eq('id', chemical.id);

      if (error) throw error;
      showSuccess('Chemical removed');
      loadChemicals();
    } catch (err) {
      showError('Failed to delete: ' + err.message);
    }
  };

  const filteredChemicals = chemicals.filter(c => {
    const term = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.manufacturer?.toLowerCase().includes(term) ||
      c.epa_number?.toLowerCase().includes(term) ||
      c.active_ingredient?.toLowerCase().includes(term)
    );
  });

  const getSignalWordColor = (word) => {
    switch (word) {
      case 'DANGER': return colors.error;
      case 'WARNING': return colors.warning;
      case 'CAUTION': return colors.info;
      default: return colors.textMuted;
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Beaker size={24} />
            Chemical Library
          </h2>
          <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
            Manage chemicals for job tracking and receipt reporting
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            padding: '0.75rem 1.25rem',
            background: colors.accent,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Plus size={18} />
          Add Chemical
        </button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <Search
          size={20}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }}
        />
        <input
          type="text"
          placeholder="Search by name, manufacturer, EPA number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem 0.75rem 3rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            fontSize: '0.95rem'
          }}
        />
      </div>

      {/* Chemical List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
          Loading chemicals...
        </div>
      ) : filteredChemicals.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `2px dashed ${colors.border}`
        }}>
          <Beaker size={48} color={colors.textMuted} style={{ marginBottom: '1rem' }} />
          <h3 style={{ color: colors.textMuted, marginBottom: '0.5rem' }}>
            {searchTerm ? 'No chemicals match your search' : 'No chemicals added yet'}
          </h3>
          <p style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
            {searchTerm ? 'Try a different search term' : 'Add chemicals you use to track them on jobs'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredChemicals.map(chemical => (
            <div
              key={chemical.id}
              style={{
                background: colors.bgCard,
                borderRadius: '10px',
                padding: '1rem 1.25rem',
                border: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
                  <span style={{ color: colors.text, fontWeight: '600', fontSize: '1rem' }}>
                    {chemical.name}
                  </span>
                  {chemical.signal_word && (
                    <span style={{
                      padding: '0.15rem 0.5rem',
                      background: `${getSignalWordColor(chemical.signal_word)}20`,
                      color: getSignalWordColor(chemical.signal_word),
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {chemical.signal_word}
                    </span>
                  )}
                  <span style={{
                    padding: '0.15rem 0.5rem',
                    background: colors.bgInput,
                    color: colors.textMuted,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    textTransform: 'capitalize'
                  }}>
                    {chemical.category}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  {chemical.manufacturer && (
                    <span>{chemical.manufacturer}</span>
                  )}
                  {chemical.epa_number && (
                    <span>EPA: {chemical.epa_number}</span>
                  )}
                  {chemical.active_ingredient && (
                    <span>Active: {chemical.active_ingredient}</span>
                  )}
                  {chemical.cost_per_unit && (
                    <span>${chemical.cost_per_unit}/{chemical.default_unit}</span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {chemical.sds_url && (
                  <a
                    href={chemical.sds_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.info,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="View Safety Data Sheet"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                <button
                  onClick={() => openEditModal(chemical)}
                  style={{
                    padding: '0.5rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.info,
                    cursor: 'pointer'
                  }}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(chemical)}
                  style={{
                    padding: '0.5rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.error,
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '550px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: colors.bgCard,
              zIndex: 1
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingChemical ? 'Edit Chemical' : 'Add Chemical'}
              </h3>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSave} style={{ padding: '1.5rem' }}>
              {/* Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Chemical Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Demand CS"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Manufacturer & EPA */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    placeholder="e.g., Syngenta"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    EPA Reg. Number
                  </label>
                  <input
                    type="text"
                    value={formData.epa_number}
                    onChange={(e) => setFormData({ ...formData, epa_number: e.target.value })}
                    placeholder="e.g., 100-1066"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
              </div>

              {/* Active Ingredient */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Active Ingredient
                </label>
                <input
                  type="text"
                  value={formData.active_ingredient}
                  onChange={(e) => setFormData({ ...formData, active_ingredient: e.target.value })}
                  placeholder="e.g., Lambda-cyhalothrin 9.7%"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Category & Signal Word */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Signal Word
                  </label>
                  <select
                    value={formData.signal_word}
                    onChange={(e) => setFormData({ ...formData, signal_word: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  >
                    {SIGNAL_WORDS.map(sw => (
                      <option key={sw.value} value={sw.value}>{sw.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Unit & Cost */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Default Unit
                  </label>
                  <select
                    value={formData.default_unit}
                    onChange={(e) => setFormData({ ...formData, default_unit: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  >
                    {UNITS.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Cost per Unit ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({ ...formData, cost_per_unit: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
              </div>

              {/* SDS URL */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Safety Data Sheet URL
                </label>
                <input
                  type="url"
                  value={formData.sds_url}
                  onChange={(e) => setFormData({ ...formData, sds_url: e.target.value })}
                  placeholder="https://..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Application tips, safety notes, etc."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: saving ? colors.border : colors.success,
                    color: saving ? colors.textMuted : '#000',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : (editingChemical ? 'Update Chemical' : 'Add Chemical')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  style={{
                    padding: '0.875rem 1.5rem',
                    background: colors.bgInput,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
